/* Hooks TanStack Query de releases do app (ADR-0659/0670/0809). */
import type {
  RegisterDiscoveredBody,
  ReleaseBulkAction,
  ReleaseChannel,
} from '@planuze/admin-contracts';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bulkReleaseAction,
  deleteRelease,
  discoverRelease,
  listAvailableVersions,
  listBucket,
  listReleases,
  promoteRelease,
  purgeReleaseVersion,
  type ReleasesQuery,
  registerDiscovered,
  takedownRelease,
  updateReleaseMandatory,
  updateReleaseNotes,
} from '../lib/releases-api.js';

const RELEASES_KEY = ['releases'] as const;
const SEMVER_RE = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z-.]+)?$/;

/* Lista paginada por cursor keyset (ADR-0659), espelhando `useCompaniesList`/`useAuditLog`:
   `initialPageParam undefined` + `getNextPageParam last.nextCursor`. Os filtros (server-side)
   compõem a queryKey — trocar filtro reinicia a paginação. As mutações invalidam `['releases']`
   (prefixo), reiniciando a lista. */
export function useReleasesList(filters: ReleasesQuery) {
  return useInfiniteQuery({
    queryKey: ['releases', 'list', filters],
    queryFn: ({ pageParam }) => listReleases({ ...filters, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export function useReleaseBucket() {
  return useQuery({ queryKey: ['releases', 'bucket'], queryFn: listBucket });
}

/* Versões disponíveis no bucket p/ o canal (ADR-0670) — powers o picker de versão. */
export function useAvailableVersions(channel: ReleaseChannel) {
  return useQuery({
    queryKey: ['releases', 'available', channel],
    queryFn: () => listAvailableVersions(channel),
  });
}

/* Descoberta dos artefatos de {canal, versão}. Habilitada só com versão semver válida. */
export function useDiscoverRelease(channel: ReleaseChannel, version: string) {
  return useQuery({
    queryKey: ['releases', 'discover', channel, version],
    queryFn: () => discoverRelease(channel, version),
    enabled: SEMVER_RE.test(version),
    retry: false,
  });
}

export function useRegisterDiscovered() {
  const invalidate = useInvalidateReleases();
  return useMutation({
    mutationFn: (body: RegisterDiscoveredBody) => registerDiscovered(body),
    onSuccess: invalidate,
  });
}

function useInvalidateReleases(): () => void {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: RELEASES_KEY });
  };
}

export function usePromoteRelease() {
  const invalidate = useInvalidateReleases();
  return useMutation({
    mutationFn: (input: { readonly id: string; readonly mandatory: boolean }) =>
      promoteRelease(input.id, input.mandatory),
    onSuccess: invalidate,
  });
}

export function useTakedownRelease() {
  const invalidate = useInvalidateReleases();
  return useMutation({ mutationFn: (id: string) => takedownRelease(id), onSuccess: invalidate });
}

/* Desindexar invalida `['releases']` inteiro (e não remove a linha do cache otimisticamente): a
   lista é paginada por cursor, e tirar um item no meio embaralharia as páginas já carregadas. */
export function useDeleteRelease() {
  const invalidate = useInvalidateReleases();
  return useMutation({ mutationFn: (id: string) => deleteRelease(id), onSuccess: invalidate });
}

/** O purge invalida lista e bucket até quando falha: o backend preserva `deleting` para retomada. */
export function usePurgeReleaseVersion() {
  const invalidate = useInvalidateReleases();
  return useMutation({
    mutationFn: (input: {
      readonly channel: ReleaseChannel;
      readonly version: string;
      readonly confirmation: string;
      readonly reason: string;
    }) => purgeReleaseVersion(input.channel, input.version, input.confirmation, input.reason),
    onSettled: invalidate,
  });
}

export function useBulkReleaseAction() {
  const invalidate = useInvalidateReleases();
  return useMutation({
    mutationFn: (input: { readonly ids: readonly string[]; readonly action: ReleaseBulkAction }) =>
      bulkReleaseAction(input.ids, input.action),
    onSuccess: invalidate,
  });
}

export function useUpdateReleaseNotes() {
  const invalidate = useInvalidateReleases();
  return useMutation({
    mutationFn: (input: {
      readonly channel: ReleaseChannel;
      readonly version: string;
      readonly releaseNotes: string | null;
    }) => updateReleaseNotes(input.channel, input.version, input.releaseNotes),
    onSuccess: invalidate,
  });
}

export function useUpdateReleaseMandatory() {
  const invalidate = useInvalidateReleases();
  return useMutation({
    mutationFn: (input: {
      readonly channel: ReleaseChannel;
      readonly version: string;
      readonly mandatory: boolean;
    }) => updateReleaseMandatory(input.channel, input.version, input.mandatory),
    onSuccess: invalidate,
  });
}
